if (!window.customElements.get('read-more-content')) {
  class ReadMoreContent extends HTMLElement {
    connectedCallback() {
      this._content = this.querySelector('.read-more__content');
      this._toggle = this.querySelector('.read-more__toggle');

      if (!this._content || !this._toggle) {
        return;
      }

      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._toggle.addEventListener('click', () => {
        this._setExpanded(!this._isExpanded);
      }, { signal });

      // In fade-only mode the toggle is visually hidden (revealed on keyboard focus),
      // so the truncated text itself doubles as the pointer target
      this._content.addEventListener('click', () => {
        if (this.classList.contains('read-more--fade') && !this.classList.contains('is-static') && !this._isExpanded) {
          this._setExpanded(true);
        }
      }, { signal });

      // Content inside a closed accordion measures 0px high — the observer re-runs the
      // measurement once the accordion opens, and again on any later size change
      // (viewport resize, late-loading fonts or images)
      this._resizeObserver = new ResizeObserver(() => this._measure());
      this._resizeObserver.observe(this._content);

      this._premeasure();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._resizeObserver?.disconnect();
    }

    get _isExpanded() {
      return this.classList.contains('is-expanded');
    }

    // AccordionDisclosure snapshots its animation target height synchronously the
    // moment the details opens — any layout change after that (like unhiding the
    // toggle) surfaces as a jump when the animation ends. Opening the details for
    // one synchronous measurement never paints, so it stays invisible and settles
    // the toggle's visibility before the first real open.
    _premeasure() {
      if (this._content.offsetHeight > 0) {
        this._measure();
        return;
      }

      const details = this.closest('details');

      if (!details || details.open) {
        return;
      }

      details.setAttribute('open', '');
      this._measure();
      details.removeAttribute('open');
    }

    _setExpanded(expanded) {
      this.classList.toggle('is-expanded', expanded);
      this._toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    _measure() {
      if (this._content.offsetHeight === 0) {
        return;
      }

      const collapsedHeight = parseFloat(window.getComputedStyle(this).getPropertyValue('--read-more-height')) || 120;

      // The tolerance keeps text that barely overflows shown in full, where expanding
      // would reveal only a line or two
      const overflows = this._content.scrollHeight > collapsedHeight + 40;
      const active = overflows || this._isExpanded;

      this.classList.toggle('is-static', !active);
      this._toggle.hidden = !active;
    }
  }

  window.customElements.define('read-more-content', ReadMoreContent);
}
